import React, { useState } from "react";
import { Space, Table, Tag } from "antd";
import { Box, Button, Modal, Fade, Backdrop, Typography } from "@mui/material";
import WarningOutlinedIcon from "@mui/icons-material/WarningOutlined";
import pdfLogo from "../../assets/images/pdfLogo.png";
import { showErrorToast, showSuccessToast } from "../utils";
import { deleteApi } from "../AppApis";
import ShareDocumentModal from "./ShareDocumentModal";

const DocumentTable = ({ dataToShow, setDataToShow, handleDownload, handleQuickView, totalPages, setCurrentPage, currentPage }) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const pageSize = 10;
    const paginatedData = dataToShow;

    function formatDate(dateString) {
        const date = new Date(dateString);

        const day = date.getDate(); // Gets the day of the month (1-31)
        const month = date.toLocaleString("default", { month: "long" }); // Full month name
        const year = date.getFullYear(); // Four-digit year

        return `${day} ${month}, ${year}`;
    }
    const columns = [
        {
            title: "Contract Name",
            dataIndex: "name",
            key: "name",
            render: (text, row) => (
                <div className="tw-flex tw-items-center tw-gap-2">
                    <img src={pdfLogo} alt="PDF Logo" className="tw-h-[30px] tw-mr-2" />
                    <div className="tw-flex tw-flex-col">
                        <p>{text}</p>
                        <p className="tw-text-[12px] tw-text-light_text">uploaded {formatDate(row.uploaded_at)}</p>
                    </div>
                </div>
            ),
        },
        {
            title: "Property Address",
            dataIndex: "property_address",
            key: "property_address",
            render: (text) => <div className="tw-min-w-[100px] max-sm:tw-text-nowrap">{text}</div>,
        },
        {
            title: "Details",
            dataIndex: "detail",
            key: "detail",
            render: (_, row) => (
                <button
                    onClick={() => {
                        handleQuickView(row);
                    }}
                    className="tw-py-[5px] tw-px-[30px] tw-rounded-lg tw-text-white  tw-bg-primary"
                >
                    Preview
                </button>
            ),
        },

        {
            title: "Action",
            key: "action",
            dataIndex: "action",

            render: (_, row) => (
                <Space size="middle">
                    <button>
                        <i className="fi fi-rs-download" onClick={() => handleDownload(row)}></i>
                    </button>
                    <button>
                        <i className="fi fi-rr-trash" onClick={() => handleDeleteClick(row)}></i>
                    </button>
                    <button>
                        <i className="fi fi-rr-share" onClick={() => handleShareClick(row)}></i>
                    </button>
                </Space>
            ),
        },
    ];
    const handleShareClick = (file) => {
        if (!file) {
            console.error("Selected file is undefined.");
            return;
        }

        // Determine the file URL based on available properties
        const fileUrl = file.fileUrl || file.url;
        if (!fileUrl) {
            console.error("File URL is missing in the selected file.");
            return;
        }
        setSelectedFile({ ...file, fileUrl });
        setShareModalOpen(true);
    };

    const handleCloseModal = () => {
        setShareModalOpen(false);
    };
    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDocumentToDelete(null);
        setIsDeleting(false);
    };

    const handleDeleteClick = (document) => {
        if (!document) {
            console.error("Document not found.");
            return;
        }
        console.log("Document selected for deletion:", document); // Debugging: log the selected document
        setDocumentToDelete(document);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        console.log("Attempting to delete document:", documentToDelete); // Log the document to delete
        if (!documentToDelete || !documentToDelete.doc_id) {
            console.error("No document selected for deletion.", documentToDelete);
            return;
        }

        // Prevent duplicate requests
        if (isDeleting) {
            return;
        }

        setIsDeleting(true);

        try {
            const token = localStorage.getItem("authUser");
            if (!token) {
                showErrorToast("User is not authenticated. Please log in.");
                return;
            }
            const response = await deleteApi(`/documents/user-documents/${documentToDelete.doc_id}`);
            showSuccessToast("Document deleted successfully", {
                onClose: () => {
                    setIsDeleteModalOpen(false);
                    setDocumentToDelete(null);
                },
            });
            setIsDeleteModalOpen(false);

            // Update the document list by removing the deleted document
            const updatedData = dataToShow.filter((doc) => doc.doc_id !== documentToDelete.doc_id);
            setDataToShow(updatedData); // Update the state with the new data
        } catch (error) {
            console.error("Delete request failed:", error);
            showErrorToast("Failed to delete the document. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Table
                columns={columns}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalPages * pageSize, // Convert pages → records
                    onChange: (page) => setCurrentPage(page),
                    className: "tw-flex tw-justify-center",
                    size: window.innerWidth < 600 ? "small" : "medium",
                    showSizeChanger: false,
                }}
                className="custom-pagination tw-overflow-x-auto tw-h-full tw-bg-white"
                dataSource={paginatedData}
            />
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={isDeleteModalOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 400,
                            bgcolor: "background.paper",
                            borderRadius: "10px",
                            boxShadow: 24,
                            p: 4,
                            outline: "none",
                            textAlign: "center",
                        }}
                    >
                        <WarningOutlinedIcon sx={{ fontSize: 60, color: "red", mb: 2 }} />
                        <Typography variant="h6" component="h2" sx={{ color: "##333333", fontWeight: "bold" }}>
                            Are you sure you want to delete this document?
                        </Typography>
                        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
                            <Button
                                onClick={handleCloseDeleteModal}
                                variant="contained"
                                sx={{
                                    bgcolor: "#28a745",
                                    "&:hover": {
                                        bgcolor: "#009721",
                                    },
                                    flex: 1,
                                    marginRight: "8px",
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                variant="contained"
                                sx={{
                                    bgcolor: isDeleting ? "#9ca3af" : "#344054",
                                    "&:hover": {
                                        bgcolor: isDeleting ? "#9ca3af" : "#5a6268",
                                    },
                                    flex: 1,
                                    marginLeft: "8px",
                                    opacity: isDeleting ? 0.7 : 1,
                                }}
                            >
                                {isDeleting ? "Deleting..." : "Delete"}
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
            <Modal open={shareModalOpen} onClose={handleCloseModal}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: "80%", md: 700 },
                        maxWidth: "90vw",
                        maxHeight: "90vh",
                        padding: 2,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: 24,
                        overflow: "auto",
                        outline: "none",
                    }}
                >
                    <ShareDocumentModal selectedFile={selectedFile} onClose={handleCloseModal} />
                    {/* <ShareComponent selectedFile={selectedFile} onClose={handleCloseModal} /> */}
                </Box>
            </Modal>
        </>
    );
};
export default DocumentTable;
